import * as turf from "@turf/turf";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  LineString,
  Point,
  Position,
} from "geojson";
import { calculateMapConfig, transformGeoJSONCoords } from "../utils/mapUtils";
import { ICON_NAMES } from "@/config/constants";
import { ScaledMapDataCacheManager } from "@/services/cache/scaledMapData/ScaledMapDataCacheManager";
import { ScaledMapData } from "@/services/cache/scaledMapData/type";
import type { MetadataItem } from "@/types/metadata";

export type MapLabelLanguage = "ja" | "en";

let metadataCache: Record<string, MetadataItem> = {};
let metadataLoaded = false;
let metadataLoading: Promise<void> | null = null;

// In-memory cache: avoids JSON.parse overhead of sessionStorage on every floor visit
const memoryCache = new Map<string, ScaledMapData>();
// Tracks in-flight fetches so concurrent requests for the same floor share one Promise
const inflight = new Map<string, Promise<ScaledMapData>>();

const ASSET_VERSION = import.meta.env.VITE_ASSET_VERSION;
const appendAssetVersion = (url: string) => {
  if (!ASSET_VERSION) {
    return url;
  }
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${ASSET_VERSION}`;
};

export const fetchMetadata = async () => {
  if (metadataLoaded) {
    return metadataCache;
  }

  if (!metadataLoading) {
    metadataLoading = fetch(appendAssetVersion("/metadata.json"))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load metadata.json: ${res.status}`);
        }
        return res.json() as Promise<MetadataItem[]>;
      })
      .then((entries) => {
        metadataCache = entries.reduce<Record<string, MetadataItem>>((acc, entry) => {
          acc[entry.id] = entry;
          return acc;
        }, {});
        metadataLoaded = true;
      })
      .catch((error) => {
        metadataLoading = null;
        throw error;
      });
  }

  await metadataLoading;
  return metadataCache;
};

const normalizeName = (value?: string) =>
  typeof value === "string" && value.trim().length > 0 ? value : undefined;

export const fetchScaledMapData = async (floorPath?: string, language: MapLabelLanguage = "ja") => {
  const geoJsonPath = floorPath || `/main-3.geojson`;
  const cacheKey = geoJsonPath + "_" + language;

  // 1. In-memory cache hit — instant, no serialization cost
  const memoryCached = memoryCache.get(cacheKey);
  if (memoryCached) return memoryCached;

  // 2. Deduplicate concurrent requests for the same floor
  const existing = inflight.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<ScaledMapData> => {
    try {
      // 3. sessionStorage fallback (covers page-reload case)
      const cacheManager = new ScaledMapDataCacheManager(cacheKey, "session");
      const cachedData = cacheManager.getCache();
      if (cachedData) {
        memoryCache.set(cacheKey, cachedData);
        return cachedData;
      }

      const polygonData = await fetch(appendAssetVersion(geoJsonPath)).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load GeoJSON: ${res.status}`);
        }
        return res.json();
      });

      // Pointとradiusを持つフィーチャをPolygonに変換
      const polygonDataWithCircles = convertCirclePointsToPolygons(polygonData);

      const metadata = await ensureMetadata();
      const { scaleFactor } = calculateMapConfig();

      const scaledPolygons = transformGeoJSONCoords(polygonDataWithCircles, scaleFactor);
      const enrichedPolygons = enrichFeaturesWithMetadata(scaledPolygons, metadata, language);

      const iconsGeoJsonPath = geoJsonPath.replace(/\.geojson$/, "-icon.geojson");
      const scaledIconsGeoJSON = await generateIconData(
        enrichedPolygons,
        iconsGeoJsonPath,
        scaleFactor,
      );
      // door-gap-renderingで利用するドアの切り欠き線分を生成
      const doorGapFeatures = createDoorGapFeatures(
        enrichedPolygons,
        scaledIconsGeoJSON,
        scaleFactor,
      );

      const scaledMapData: ScaledMapData = {
        scaledPolygons: enrichedPolygons,
        scaledIconsGeoJSON,
        doorGapFeatures,
      };
      memoryCache.set(cacheKey, scaledMapData);
      cacheManager.setCache(scaledMapData);

      return scaledMapData;
    } catch (error) {
      console.error("GeoJSONデータの読み込み・変換に失敗:", error);
      throw error;
    } finally {
      inflight.delete(cacheKey);
    }
  })();

  inflight.set(cacheKey, promise);
  return promise;
};

/** バックグラウンドで複数フロアのデータをプリフェッチする */
export const prefetchFloors = (floors: { path: string }[], language: MapLabelLanguage): void => {
  for (const floor of floors) {
    fetchScaledMapData(floor.path, language).catch(() => {});
  }
};

const ensureMetadata = async () => {
  return await fetchMetadata();
};

const getLocalizedRoomName = (
  metadataEntry: MetadataItem | undefined,
  featureId: string,
  language: MapLabelLanguage,
  roomFromFeature?: string,
) => {
  const fallbackName = roomFromFeature ?? featureId;
  if (!metadataEntry) {
    return fallbackName;
  }

  const japaneseName = normalizeName(metadataEntry.name_japanese) ?? roomFromFeature ?? featureId;
  const englishName = normalizeName(metadataEntry.name_english) ?? roomFromFeature ?? featureId;

  return language === "en"
    ? (englishName ?? japaneseName ?? fallbackName)
    : (japaneseName ?? englishName ?? fallbackName);
};

const enrichFeaturesWithMetadata = (
  collection: FeatureCollection,
  metadata: Record<string, MetadataItem>,
  language: MapLabelLanguage,
): FeatureCollection => {
  const enhancedFeatures = collection.features.map((feature) => {
    const properties: GeoJsonProperties = {
      ...(feature.properties ?? {}),
    };
    const featureId = typeof properties?.id === "string" ? properties.id : "";
    const metadataEntry = featureId ? metadata[featureId] : undefined;
    const roomFromFeature =
      typeof properties?.room === "string" && properties.room.trim().length > 0
        ? properties.room
        : undefined;
    const suppressLabelFromMetadata = metadataEntry?.suppress_label;
    const suppressLabelFromFeature =
      typeof properties?.suppress_label === "boolean" ? properties.suppress_label : undefined;
    const localizedRoom = getLocalizedRoomName(metadataEntry, featureId, language, roomFromFeature);
    const roomJa = normalizeName(metadataEntry?.name_japanese) ?? roomFromFeature ?? featureId;
    const roomEn = normalizeName(metadataEntry?.name_english) ?? roomJa;

    return {
      ...feature,
      properties: {
        ...properties,
        category:
          metadataEntry?.category ??
          (typeof properties?.category === "string" ? properties.category : ""),
        room: localizedRoom,
        room_japanese: roomJa,
        room_english: roomEn,
        suppress_label:
          typeof suppressLabelFromMetadata === "boolean"
            ? suppressLabelFromMetadata
            : suppressLabelFromFeature,
      },
    } as Feature;
  });

  return {
    ...collection,
    features: enhancedFeatures,
  };
};

/**
 * 中心座標と半径から円形のポリゴン座標を生成します。
 * @param center - 中心の座標 [number, number]
 * @param radius - 円の半径
 * @param segments - 円を近似するための頂点数
 * @returns GeoJSONのPolygon形式の座標配列
 */
const createCirclePolygon = (
  center: [number, number],
  radius: number,
  segments = 64,
): [number, number][][] => {
  const [cx, cy] = center;
  const coordinates: [number, number][] = [];

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    coordinates.push([x, y]);
  }

  return [coordinates];
};

/**
 * Pointジオメトリとradiusプロパティを持つフィーチャをPolygonに変換します。
 */
const convertCirclePointsToPolygons = (collection: FeatureCollection): FeatureCollection => {
  const convertedFeatures = collection.features.map((feature) => {
    if (
      feature.geometry.type === "Point" &&
      feature.properties &&
      typeof feature.properties.radius === "number"
    ) {
      const center = feature.geometry.coordinates as [number, number];
      const { radius } = feature.properties;
      feature.geometry = {
        type: "Polygon",
        coordinates: createCirclePolygon(center, radius),
      };
    }
    return feature;
  });

  return { ...collection, features: convertedFeatures };
};

const generateIconData = async (
  enrichedPolygons: FeatureCollection,
  iconsGeoJsonPath: string,
  scaleFactor: number,
): Promise<FeatureCollection> => {
  //アイコン用のgeojsonファイルから読み込み
  const scaledIconsDataGeneratedFromIconsGeoJSON = await generateIconsDataFromIconsGeoJSON(
    iconsGeoJsonPath,
    scaleFactor,
  );
  //マップ用のgeojsonデータからアイコン生成
  const scaledIconsDataCreatedFromPolygons = createIconsDataFromScaledPolygons(
    enrichedPolygons,
    ICON_NAMES,
  );
  //２つのアイコンデータを結合して返す
  const scaledIconsGeoJSON: FeatureCollection = {
    type: "FeatureCollection",
    features: [
      ...scaledIconsDataGeneratedFromIconsGeoJSON.features,
      ...scaledIconsDataCreatedFromPolygons.features,
    ],
  };

  return scaledIconsGeoJSON;
};

const createIconsDataFromScaledPolygons = (
  enrichedPolygons: FeatureCollection,
  targetCategories: string[],
): FeatureCollection => {
  // 1. カテゴリでフィルタリング
  const filteredFeatures = enrichedPolygons.features.filter((feature) =>
    targetCategories.includes(feature.properties?.category),
  );

  // 2. フィルタリングされた各ポリゴンを座標が重心のPointに変換
  const pointFeatures = filteredFeatures.map((polygonFeature) => {
    // 3. ポリゴン内に収まる代表点を計算
    const point = turf.pointOnFeature(polygonFeature);

    // 4. 新しいPointフィーチャーのプロパティを構築
    point.properties = {
      id: polygonFeature.properties?.id,
      category: polygonFeature.properties?.category,
    };

    return point;
  });

  const scaledIconsDataCreatedFromPolygons = turf.featureCollection(pointFeatures);

  // 5. 新しいPointフィーチャーをFeatureCollectionとして返す
  return scaledIconsDataCreatedFromPolygons;
};

const generateIconsDataFromIconsGeoJSON = async (
  iconsGeoJsonPath: string,
  scaleFactor: number,
): Promise<FeatureCollection> => {
  let iconsDataGeneratedFromIconsGeoJSON;
  try {
    iconsDataGeneratedFromIconsGeoJSON = await fetch(iconsGeoJsonPath).then((res) => {
      if (!res.ok) throw new Error(`Failed to load icons GeoJSON: ${res.status}`);
      return res.json();
    });
  } catch (error) {
    // ファイル読み込みに失敗した場合は空のFeatureCollection
    console.warn(error);
    iconsDataGeneratedFromIconsGeoJSON = {
      type: "FeatureCollection",
      features: [],
    };
  }
  return transformGeoJSONCoords(iconsDataGeneratedFromIconsGeoJSON, scaleFactor);
};

// 扉線分を描画時に拡大率を掛けるための基準値（通常の開口部長さ）
const DOOR_GAP_LENGTH_UNSCALED = 10;
// 特定ドア(ID指定)で使用する開口部長さ（表示上 約31px 相当）
const LONG_DOOR_GAP_LENGTH_UNSCALED = 31;
// 短いバージョン
const SHORT_DOOR_GAP_LENGTH_UNSCALED = 3;

const SHORT_DOOR_IDS = [
  "M-4-womens_restroom-003",
  "M-4-mens_restroom-003",
  "M-4-barrier_free_restroom-002",
  "M-5-womens_restroom-002",
  "M-5-mens_restroom-002",
  "M-5-barrier_free_restroom-002",
  "M-5-mens_restroom-003",
  "M-5-womens_restroom-003",
  "R-2-mens_restroom-001",
  "R-2-womens_restroom-001",
  "R-2-barrier_free_restroom-001",
  "R-2-mens_restroom-002",
  "R-2-womens_restroom-002",
  "R-2-facility-001",
  "R-1-mens_restroom-001",
  "R-1-womens_restroom-001",
  "R-1-barrier_free_restroom-001",
];
const DOOR_GAP_THRESHOLD_UNSCALED = 55;

const createDoorGapFeatures = (
  polygons: FeatureCollection,
  icons: FeatureCollection,
  scaleFactor: number,
): FeatureCollection => {
  // ドアアイコンに最も近い壁の線分を探し、切り欠き用の短いLineStringを生成
  const doorPoints = icons.features.filter(
    (feature): feature is Feature<Point> =>
      feature.geometry?.type === "Point" && feature.properties?.category === "indoor_entrance",
  );

  if (doorPoints.length === 0) {
    return turf.featureCollection([]);
  }

  const boundaryLines = extractPolygonBoundaryLines(polygons);
  if (boundaryLines.length === 0) {
    return turf.featureCollection([]);
  }

  const snapThreshold = DOOR_GAP_THRESHOLD_UNSCALED * scaleFactor;

  const gapSegments: Feature<LineString>[] = [];

  doorPoints.forEach((doorPoint, doorIndex) => {
    const doorCoords = doorPoint.geometry.coordinates as [number, number];
    let bestMatch: {
      coords: Position[];
      snapped: [number, number];
      segmentIndex: number;
      distance: number;
    } | null = null;

    for (const { line, coords } of boundaryLines) {
      const snappedPoint = turf.nearestPointOnLine(line, doorPoint);
      const snappedCoords = snappedPoint.geometry.coordinates as [number, number];
      const euclideanDistance = Math.hypot(
        doorCoords[0] - snappedCoords[0],
        doorCoords[1] - snappedCoords[1],
      );

      if (!bestMatch || euclideanDistance < bestMatch.distance - Number.EPSILON) {
        const props = snappedPoint.properties as { index?: number } | null;
        const rawIndex = props?.index;
        const segmentIndex = typeof rawIndex === "number" ? rawIndex : -1;

        bestMatch = {
          coords,
          snapped: snappedCoords,
          segmentIndex: segmentIndex,
          distance: euclideanDistance,
        };
      }
    }

    if (!bestMatch || bestMatch.distance > snapThreshold || bestMatch.segmentIndex < 0) {
      return;
    }

    const segmentStart = bestMatch.coords[bestMatch.segmentIndex];
    const segmentEnd = bestMatch.coords[bestMatch.segmentIndex + 1];
    if (!segmentStart || !segmentEnd) {
      return;
    }

    const dx = segmentEnd[0] - segmentStart[0];
    const dy = segmentEnd[1] - segmentStart[1];
    const segmentLength = Math.hypot(dx, dy);
    if (segmentLength === 0) {
      return;
    }

    const unitX = dx / segmentLength;
    const unitY = dy / segmentLength;

    const snappedCoord = bestMatch.snapped;
    const vectorFromStart = [
      snappedCoord[0] - segmentStart[0],
      snappedCoord[1] - segmentStart[1],
    ] as [number, number];
    const projectionOnSegment = vectorFromStart[0] * unitX + vectorFromStart[1] * unitY;

    const doorId = doorPoint.properties?.id;

    // デフォルトの長さ (10)
    let targetUnscaledLength = DOOR_GAP_LENGTH_UNSCALED;

    if (doorId === "M-3-facility-009") {
      // 特定の長いドア
      targetUnscaledLength = LONG_DOOR_GAP_LENGTH_UNSCALED;
    } else if (doorId && SHORT_DOOR_IDS.includes(doorId)) {
      // 配列に含まれているかチェック
      targetUnscaledLength = SHORT_DOOR_GAP_LENGTH_UNSCALED;
    }

    // 半分の長さにスケールを掛ける
    const effectiveHalfGapLength = (targetUnscaledLength / 2) * scaleFactor;
    const backwardLength = Math.min(effectiveHalfGapLength, Math.max(projectionOnSegment, 0));
    const forwardLength = Math.min(
      effectiveHalfGapLength,
      Math.max(segmentLength - projectionOnSegment, 0),
    );

    const gapStart: [number, number] = [
      snappedCoord[0] - unitX * backwardLength,
      snappedCoord[1] - unitY * backwardLength,
    ];
    const gapEnd: [number, number] = [
      snappedCoord[0] + unitX * forwardLength,
      snappedCoord[1] + unitY * forwardLength,
    ];

    gapSegments.push(
      turf.lineString([gapStart, gapEnd], {
        category: "door_gap",
        sourceDoorIndex: doorIndex,
      }),
    );
  });

  return turf.featureCollection(gapSegments);
};

// ドアギャップ計算時に壁の輪郭線を参照するためのユーティリティ
const extractPolygonBoundaryLines = (
  collection: FeatureCollection,
): { line: Feature<LineString>; coords: Position[] }[] => {
  const boundaries: { line: Feature<LineString>; coords: Position[] }[] = [];

  collection.features.forEach((feature) => {
    if (!feature.geometry) return;

    if (feature.geometry.type === "Polygon") {
      feature.geometry.coordinates.forEach((ring) => {
        if (ring.length < 2) return;
        boundaries.push({
          line: turf.lineString(ring),
          coords: ring,
        });
      });
    } else if (feature.geometry.type === "MultiPolygon") {
      feature.geometry.coordinates.forEach((polygon) => {
        polygon.forEach((ring) => {
          if (ring.length < 2) return;
          boundaries.push({
            line: turf.lineString(ring),
            coords: ring,
          });
        });
      });
    }
  });

  return boundaries;
};
